import BookIcon from '@mui/icons-material/Book';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CircularProgress from '@mui/material/CircularProgress';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SecureImage } from '@/components/secure-image';
import { useNovel } from '@/context/novel-context';
import type { NarrativeSummary } from '@/lib/types';
import { ReaderSidebar } from './ReaderSidebar';

interface NovelDetailsViewProps {
  novelId: string;
}

export function NovelDetailsView({ novelId }: NovelDetailsViewProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { novel, isLoading, setSelectedNovelId } = useNovel();
  const [lastReadChapterId, setLastReadChapterId] = useState<string | null>(null);

  useEffect(() => {
    if (novelId) {
      setSelectedNovelId(novelId);
    }
  }, [novelId, setSelectedNovelId]);

  useEffect(() => {
    try {
      const stored = localStorage.getItem('eplotera_reading_progress');
      if (stored && novelId) {
        const map = JSON.parse(stored);
        if (map[novelId]?.chapterId) {
          setLastReadChapterId(map[novelId].chapterId);
        }
      }
    } catch (e) {
      console.error('Failed to load reading progress', e);
    }
  }, [novelId]);

  const chaptersList = useMemo(() => {
    if (!novel?.summaries?.chapters) return [];
    return (Object.values(novel.summaries.chapters) as NarrativeSummary[])
      .filter((ch) => !ch.isArchive && (ch.publishStatus === 'preview' || ch.publishStatus === 'public'))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [novel]);

  const handleStartOrContinue = () => {
    if (chaptersList.length === 0) return;
    const targetChapterId = lastReadChapterId && chaptersList.some(c => c.id === lastReadChapterId)
      ? lastReadChapterId
      : chaptersList[0].id;

    navigate({
      to: '/read/$novelId/$chapterId',
      params: { novelId, chapterId: targetChapterId },
    });
  };

  if (isLoading || !novel.id) {
    return (
      <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
        <ReaderSidebar />
        <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  const coverImage = novel.coverImage;
  const coverImageUrl = coverImage?.original.url || novel.coverImageUrl;
  const coverImagePath = coverImage?.original.path || coverImage?.profile.path;

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default', color: 'text.primary' }}>
      <ReaderSidebar />
      <Box sx={{ flexGrow: 1, p: 4, overflowY: 'auto' }}>
        {/* Book Header Card */}
        <Card
          sx={{
            p: 3,
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 4,
          }}
        >
          {/* Cover Art */}
          <Box
            sx={{
              width: { xs: '100%', md: 200 },
              aspectRatio: '2/3',
              borderRadius: 2,
              overflow: 'hidden',
              bgcolor: 'transparent',
              flexShrink: 0,
              boxShadow: (theme) => theme.shadows[3],
              alignSelf: { xs: 'stretch', md: 'flex-start' },
            }}
          >
            {coverImageUrl ? (
              <SecureImage
                src={coverImageUrl}
                path={coverImagePath}
                alt={novel.title}
                sx={{ objectFit: 'contain' }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  p: 3,
                  textAlign: 'center',
                  background: (theme) =>
                    theme.palette.mode === 'dark'
                      ? 'linear-gradient(135deg, #1f2937 0%, #111827 100%)'
                      : 'linear-gradient(135deg, #f3f4f6 0%, #e5e7eb 100%)',
                }}
              >
                <BookIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1.5 }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {novel.title}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Details */}
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-between', flexGrow: 1 }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 1 }}>
                {novel.title}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 2 }}>
                {t('reader.details.chapters_count', { count: chaptersList.length })}
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3, whiteSpace: 'pre-wrap' }}>
                {novel.description || t('reader.details.no_description')}
              </Typography>
            </Box>

            <Box>
              <Button
                variant="contained"
                size="large"
                startIcon={<PlayArrowIcon />}
                disabled={chaptersList.length === 0}
                onClick={handleStartOrContinue}
                sx={{ borderRadius: 2, px: 4, py: 1.5 }}
              >
                {lastReadChapterId ? t('reader.details.continue') : t('reader.details.start')}
              </Button>
            </Box>
          </Box>
        </Card>

        {/* Chapters Index */}
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em', mb: 2 }}>
            {t('reader.details.chapters_title')}
          </Typography>

          {chaptersList.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center', bgcolor: 'background.paper', borderRadius: 3, border: '1px dashed', borderColor: 'divider' }}>
              <Typography variant="body1" color="text.secondary">
                {t('reader.details.no_chapters')}
              </Typography>
            </Box>
          ) : (
            <Card sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
              <List disablePadding>
                {chaptersList.map((chapter, index) => {
                  const isLastRead = lastReadChapterId === chapter.id;

                  return (
                    <ListItem
                      key={chapter.id}
                      divider={index < chaptersList.length - 1}
                      disablePadding
                    >
                      <Link
                        to="/read/$novelId/$chapterId"
                        params={{ novelId, chapterId: chapter.id }}
                        style={{ textDecoration: 'none', color: 'inherit', width: '100%' }}
                      >
                        <ListItemButton
                          sx={{
                            py: 2,
                            px: 3,
                            bgcolor: isLastRead ? 'action.selected' : 'transparent',
                            '&:hover': {
                              bgcolor: 'action.hover',
                            },
                          }}
                        >
                          <ListItemText
                            primary={chapter.name || `Chapter ${index + 1}`}
                            primaryTypographyProps={{
                              fontWeight: isLastRead ? 700 : 500,
                              color: 'text.primary',
                            }}
                            secondary={chapter.publishStatus === 'preview' ? 'Preview' : undefined}
                            secondaryTypographyProps={{
                              color: 'primary.main',
                              fontSize: '0.75rem',
                              fontWeight: 700,
                              textTransform: 'uppercase',
                            }}
                          />
                          <ChevronRightIcon sx={{ color: 'text.secondary' }} />
                        </ListItemButton>
                      </Link>
                    </ListItem>
                  );
                })}
              </List>
            </Card>
          )}
        </Box>
      </Box>
    </Box>
  );
}
